'use client';

import { cn, useBookCatalogContext } from '@sharpr/ui';
import { NBA_RESULTS } from './nbaResults.generated';
import { sportsbookLogos } from './SportsbookStrip';

// Signup-page proof: a quiet block under the headline (desktop only).

const EDGE10 = NBA_RESULTS.tiers.edge10;

const units = (u: number) => `${u >= 0 ? '+' : '−'}${Math.abs(u).toFixed(1)}u`;
const pct = (f: number) => `${f >= 0 ? '+' : '−'}${Math.abs(f * 100).toFixed(1)}%`;

// Day 0 = zero before launch night, so the line starts on the baseline.
const CURVE = [0, ...EDGE10.curve.map((p) => p.units)];

// --- Desktop: quiet proof block ----------------------------------------------------

// Four books a prop bettor knows on sight; rendered as one monochrome row.
const LOGO_KEYS = ['draftkings', 'betmgm', 'caesars', 'novig'];
const LOGOS = LOGO_KEYS.flatMap((k) => sportsbookLogos.filter((l) => l.key === k));

/** The 10%+ result, its curve as a thin line, three supporting numbers, and the books. */
export function SignupProofBlock() {
  const catalog = useBookCatalogContext();
  const stats = [
    { label: 'Record', value: `${EDGE10.wins}–${EDGE10.losses}` },
    { label: 'ROI', value: pct(EDGE10.roi) },
    { label: 'Beat the close', value: `${Math.round((EDGE10.beatClose ?? 0) * 100)}%` },
  ];
  return (
    <div>
      <div className="border-t border-border dark:border-border-secondary pt-6">
        <div className="grid grid-cols-3 items-end gap-6">
          <div className="col-span-2">
            <p className="text-4xl font-bold tracking-tight text-emerald-600 dark:text-emerald-400 tabular-nums">
              {units(EDGE10.units)}
            </p>
            <p className="mt-1 text-sm text-fg/60">since launch on our 10%+ NBA picks</p>
          </div>
          <Sparkline className="h-12 w-full" />
        </div>
        <dl className="mt-6 grid grid-cols-3 gap-6">
          {stats.map((s) => (
            <div key={s.label}>
              <dt className="text-sm text-fg/50">{s.label}</dt>
              <dd className="mt-0.5 text-base font-semibold text-fg tabular-nums">{s.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="mt-8 border-t border-border dark:border-border-secondary pt-6">
        <p className="text-sm font-medium text-fg">Free odds screen with every account</p>
        <p className="mt-1 text-sm text-fg/55">Live prop prices across the major books and exchanges.</p>
        <div className="mt-4 flex items-center gap-6">
          {LOGOS.map((logo) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              key={logo.key}
              src={logo.src}
              alt={catalog.get(logo.key)?.name ?? logo.key}
              width={120}
              height={24}
              className={cn(
                'w-auto max-w-24 object-contain grayscale',
                // Novig's solid white wordmark reads louder than the rest — a step smaller and dimmer.
                logo.key === 'novig' ? 'h-[13px] opacity-40' : 'h-[18px] opacity-60',
              )}
            />
          ))}
          <span className="whitespace-nowrap text-sm text-fg/50">and more</span>
        </div>
      </div>
    </div>
  );
}

function Sparkline({ className }: { className?: string }) {
  const lo = Math.min(0, ...CURVE);
  const hi = Math.max(...CURVE);
  const W = 100;
  const H = 32;
  const x = (i: number) => (i / (CURVE.length - 1)) * W;
  const y = (u: number) => H - ((u - lo) / (hi - lo)) * H;
  const line = CURVE.map((u, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(2)},${y(u).toFixed(2)}`).join('');
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden className={cn('overflow-visible', className)}>
      <path
        d={line}
        fill="none"
        stroke="var(--brand-500)"
        strokeWidth={2}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
